import {
    Button,
    Dialog,
    DialogBackdrop,
    DialogPanel,
    DialogTitle,
    Input,
    Menu, MenuButton, MenuItem, MenuItems,
    Select,
    Textarea
} from "@headlessui/react";
import React from "react";
import {useEffect, useState} from "react";
import {
    ArrowPathIcon, CreditCardIcon, IdentificationIcon,
    PlusCircleIcon,
    PlusIcon,
    TrashIcon,
    UserPlusIcon,
    XCircleIcon, XMarkIcon
} from "@heroicons/react/24/outline";
import {db} from "../../firebase/firebase";
import {useAuth} from "../../contexts/AuthProvider";
import { format } from 'date-fns';
import {formatPhoneNumber} from "../GlobalFunctions";
import { ReactComponent as LOGO } from "../../Logo.svg";
import {EllipsisVerticalIcon, ExclamationTriangleIcon} from "@heroicons/react/16/solid";

function classNames(...classes) {
    return classes.filter(Boolean).join(' ')
}


const Customers = () => {

    const { currentUser } = useAuth();

    const [open, setOpen] = useState(false);

    const [openDialogId, setOpenDialogId] = useState(null);

    const [name, setName] = useState('');
    const [phone, setPhone] = useState('');
    const [email, setEmail] = useState('');
    const [children, setChildren] = useState('');
    const [category, setCategory] = useState('');
    const [referral, setReferral] = useState('');
    const [notes, setNotes] = useState('');
    const [childrenList, setChildrenList] = useState([]);

    const lastUpdated = localStorage.getItem(`customers_last_updated_${currentUser.uid}`);

    const handleSubmit = async (e) => {
        e.preventDefault();
        const collectionRef = db.collection('Customers');

        const parsedFormData = {
            name: name,
            phone: phone,
            email: email,
            children: parseFloat(children),
            referral: referral,
            category: category,
            notes: notes,
            childrenList: childrenList,
        };

        try {
            // Add the new document to Firestore
            await collectionRef.add(parsedFormData);

            console.log('customer added successfully!');

            await clearForm();

            await onUpdate();

            setOpen(false);

            // clearForm(); // Clear the form after submission
        } catch (error) {
            console.error('Error adding/customer: ', error);
        }
    };

    const clearForm = async () => {
        setName('');
        setPhone('');
        setEmail('');
        setChildren('');
        setReferral('');
        setCategory('');
        setNotes('');
        setChildrenList([]);
    }

    const [customers, setCustomers] = useState([]);

    useEffect(() => {
        const fetchCustomers = async () => {
            if (currentUser) {
                try {
                    const storedInventoryData = localStorage.getItem(`customers_${currentUser.uid}`);
                    if (storedInventoryData) {
                        const inventoryData = JSON.parse(storedInventoryData);
                        setCustomers(inventoryData);
                    } else {
                        const inventoryCollectionRef = db.collection('Customers');
                        const snapshot = await inventoryCollectionRef.get();

                        if (!snapshot.empty) {
                            const inventoryData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                            setCustomers(inventoryData);
                            localStorage.setItem(`customers_${currentUser.uid}`, JSON.stringify(inventoryData));
                        } else {
                            console.log('No customers found');
                        }
                    }
                } catch (error) {
                    console.error('Error fetching inventory:', error);
                }
            }
        };

        fetchCustomers();

    }, [currentUser]);

    const onUpdate = async () => {
        const inventoryCollectionRef = db.collection('Customers');
        const snapshot = await inventoryCollectionRef.get();

        if (!snapshot.empty) {
            const inventoryData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setCustomers(inventoryData);
            localStorage.setItem(`customers_${currentUser.uid}`, JSON.stringify(inventoryData));


            // Format and save last updated timestamp
            const now = new Date();
            const formatted = format(now, 'MMM dd, yyyy \'at\' hh:mm a');
            localStorage.setItem(`customers_last_updated_${currentUser.uid}`, formatted);

        } else {
            console.log('No inventory found');
        }
    };

    const deleteCust = async (id) => {
        try {
            await db.collection('Customers').doc(id).delete();

            await onUpdate();

            console.log(`Customer with id ${id} deleted`);
        } catch (error) {
            console.error('Error deleting customer:', error);
        }
    };



    const handleChildChange = (index, fieldName, e) => {
        const { value } = e.target;
        const parsedValue = fieldName === 'age' ? parseFloat(value) : value;

        setChildrenList(prevChilds =>
            prevChilds.map((kid, i) =>
                i === index ? { ...kid, [fieldName]: parsedValue } : kid
            )
        );
    };

    const handleAddChild = () => {
        setChildrenList(prev => [...prev, { name: '', age: '' }]);
    };

    const handleRemoveChild = (indexToRemove) => {
        setChildrenList(prev => prev.filter((_, index) => index !== indexToRemove));
    };




    return (
        <div className="">
            <div className="p-4 animate-fadeIn bg-white h-dvh">

                {/*Header*/}
                <div className="sm:flex sm:items-center">
                    <div className="sm:flex-auto">
                        <h1 className="text-base flex items-center font-semibold text-gray-900">Shoppers
                            [{customers.length}]

                            <Button onClick={onUpdate} className={`ml-3`}>
                                <ArrowPathIcon
                                    className={`size-4 text-gray-500 stroke-2 hover:stroke-2 hover:rotate-12`}/>
                            </Button>
                        </h1>
                        <p className="mt-2 text-sm text-gray-700">
                            This is all of the <strong className="font-semibold text-gray-900">Shoppers</strong> you
                            have in your cloud that you have helped.
                            The last time this was updated was <span className={`font-semibold`}>{lastUpdated}</span>
                        </p>
                    </div>
                    <div className="mt-4 sm:ml-16 sm:mt-0 sm:flex-none">
                        <Button
                            type="button"
                            onClick={() => setOpen(true)}
                            className="flex items-center gap-2 rounded-md bg-blue-600 px-3 py-2 text-center text-sm font-semibold text-white shadow-sm hover:bg-opacity-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
                        >
                            <PlusIcon className={`size-4`}/>
                            New Shopper
                        </Button>
                    </div>
                </div>

                {/*Body*/}
                <div className="mt-8 flow-root">
                    <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                        <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                            <div className="overflow-x-auto [&::-webkit-scrollbar]:h-2 [&::-webkit-scrollbar-thumb]:rounded-none">
                              <div className="min-w-full">
                                <table className="min-w-full">
                                  <thead>
                                    <tr className="border-b border-gray-200">
                                      <th className="pb-3 text-left text-xs uppercase text-gray-400">Shoppers</th>
                                      <th className="pb-3 text-left text-xs uppercase text-gray-400">Status</th>
                                      <th className="pb-3 text-left text-xs uppercase text-gray-400">Children</th>
                                      <th className="pb-3 text-left text-xs uppercase text-gray-400">Referral</th>
                                      <th></th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-gray-100">
                                    {customers.map((cust) => (
                                      <tr key={cust.id} className="hover:bg-gray-50 transition align-top">
                                        {/* Shopper */}
                                        <td className="py-4">
                                          <div className="flex items-start gap-3">
                                            <div className="size-9 rounded-full bg-gray-100 flex items-center justify-center">
                                              <span className="text-xs font-semibold text-gray-600">
                                                {cust.name?.charAt(0) || "U"}
                                              </span>
                                            </div>

                                            <div className="space-y-1">
                                              <div className="font-medium text-gray-900">
                                                {cust.name}
                                              </div>

                                              <div className="text-xs text-gray-500">
                                                {cust.email || "No email"}
                                              </div>

                                              <div className="text-xs text-gray-500">
                                                {formatPhoneNumber(cust.phone) || "No phone"}
                                              </div>

                                              {cust.notes && (
                                                <div className="text-xs text-gray-400 italic max-w-xs">
                                                  {cust.notes}
                                                </div>
                                              )}
                                            </div>
                                          </div>
                                        </td>

                                        {/* Status */}
                                        <td className="py-4">
                                          <div className="flex items-center gap-2">
                                            <span className="size-2 bg-green-500 rounded-full"></span>
                                            <span className="text-sm text-gray-800">Active</span>
                                          </div>

                                          <div className="text-xs text-gray-400 mt-1">
                                            ID: {cust.id}
                                          </div>
                                        </td>

                                        {/* Children */}
                                        <td className="py-4 text-sm text-gray-800">
                                          <div className="font-medium">
                                            {cust.children || 0}
                                          </div>

                                          <div className="mt-2 space-y-1 text-xs text-gray-500">
                                            {cust.childrenList?.map((child, idx) => (
                                              <div key={idx} className="flex justify-between">
                                                <span>{child.name}</span>
                                                <span>{child.age}</span>
                                              </div>
                                            ))}
                                          </div>
                                        </td>

                                        {/* Referral */}
                                        <td className="py-4 text-sm text-gray-600">
                                          {cust.referral || "-"}

                                          {cust.category && (
                                            <div className="text-xs text-gray-400 mt-1">
                                              {cust.category}
                                            </div>
                                          )}
                                        </td>

                                        {/* Actions */}
                                        <td className="py-4 text-right">
                                          <button
                                            onClick={() => deleteCust(cust.id)}
                                            className="text-red-500 text-sm hover:underline"
                                          >
                                            Delete
                                          </button>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                    </div>
                </div>
            </div>

        </div>


    <Dialog open={open} onClose={setOpen} className="relative z-50">
        <DialogBackdrop
            transition
            className="fixed inset-0 bg-gray-500/75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
        />

        <div className="fixed inset-0"/>

        <div className="fixed inset-0 overflow-hidden">
            <div className="absolute inset-0 overflow-hidden">
                        <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10">
                            <DialogPanel
                                transition
                                className="pointer-events-auto w-screen max-w-md transform transition duration-500 ease-in-out data-[closed]:translate-x-full sm:duration-700"
                            >
                                <div className="flex h-full flex-col overflow-y-scroll bg-white py-2 shadow-xl">

                                    {/*Drawer Header*/}
                                    <div className={`border-b-2 border-dashed px-4 py-4 sm:px-6`}>
                                        <div className="flex items-center justify-between">
                                            <div className="mx-auto flex size-12 shrink-0 items-center justify-center rounded-full bg-blue-600/15 sm:mx-0 sm:size-10">
                                                <LOGO aria-hidden="true" className="size-6 text-blue-600"/>
                                            </div>

                                            <DialogTitle className="text-base font-semibold tracking-widest">
                                                Shopper Form
                                            </DialogTitle>

                                            <div className="ml-3 flex h-7 items-center">
                                                <Button
                                                    type="button"
                                                    onClick={() => clearForm()}
                                                    className="relative rounded-md hover:text-red-500 hover:animate-heartbeat"
                                                >
                                                    <span className="absolute -inset-2.5"/>
                                                    <span className="sr-only">Clear Form</span>
                                                    <XCircleIcon aria-hidden="true" className="size-6"/>
                                                </Button>
                                            </div>
                                        </div>
                                    </div>


                                    <div className="relative mt-6 flex-1 px-4 sm:px-6">

                                        <div className={`grid grid-cols-6 mt-3 gap-4`}>

                                            {/*Name*/}
                                            <div className="col-span-3 relative">
                                                <label
                                                    className="absolute -top-2 left-2 inline-block rounded-lg bg-white px-1 text-xs font-medium text-gray-900"
                                                >
                                                    Name
                                                </label>
                                                <Input
                                                    type="text"
                                                    value={name}
                                                    onChange={(e) => setName(e.target.value)}
                                                    placeholder="Customer Name"
                                                    className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-blue-600 sm:text-sm/6"
                                                />
                                            </div>

                                            {/*Phone*/}
                                            <div className="col-span-3 relative">
                                                <label
                                                    className="absolute -top-2 left-2 inline-block rounded-lg bg-white px-1 text-xs font-medium text-gray-900"
                                                >
                                                    Phone
                                                </label>
                                                <Input
                                                    type="tel"
                                                    value={phone}
                                                    onChange={(e) => setPhone(e.target.value)}
                                                    placeholder="Customer Phone Number"
                                                    className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-blue-600 sm:text-sm/6"
                                                />
                                            </div>

                                            {/*Email*/}
                                            <div className="col-span-full relative">
                                                <label
                                                    className="absolute -top-2 left-2 inline-block rounded-lg bg-white px-1 text-xs font-medium text-gray-900"
                                                >
                                                    Email
                                                </label>
                                                <Input
                                                    type="email"
                                                    value={email}
                                                    onChange={(e) => setEmail(e.target.value)}
                                                    placeholder="Customer Email"
                                                    className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-blue-600 sm:text-sm/6"
                                                />
                                            </div>

                                            {/*Referral*/}
                                            <div className="col-span-full relative">
                                                <label
                                                    className="absolute -top-2 left-2 inline-block rounded-lg bg-white px-1 text-xs font-medium text-gray-900"
                                                >
                                                    Referral
                                                </label>
                                                <Input
                                                    type="text"
                                                    value={referral}
                                                    onChange={(e) => setReferral(e.target.value)}
                                                    placeholder="Referral"
                                                    className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-blue-600 sm:text-sm/6"
                                                />
                                            </div>

                                            {/*Children*/}
                                            <div className="col-span-full relative">
                                                <label
                                                    className="absolute -top-2 left-2 inline-block rounded-lg bg-white px-1 text-xs font-medium text-gray-900"
                                                >
                                                    Number of Children
                                                </label>
                                                <Input
                                                    type="number"
                                                    value={children}
                                                    onChange={(e) => setChildren(e.target.value)}
                                                    placeholder="Children"
                                                    className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-blue-600 sm:text-sm/6"
                                                />
                                            </div>

                                            {/*Children Button*/}
                                            <div className="col-span-full relative">
                                                <div className="flex items-center justify-between">
                                                    <div className="mx-auto flex size-8 shrink-0 items-center justify-center rounded-full bg-blue-600/15 sm:mx-0 sm:size-8">
                                                        <UserPlusIcon aria-hidden="true" className="size-4 stroke-2 text-blue-600"/>
                                                    </div>

                                                    <div className="text-base font-semibold tracking-widest">
                                                        Children [{childrenList.length}]
                                                    </div>

                                                    <div className="ml-3 flex h-7 items-center">
                                                        <Button
                                                            type="button"
                                                            onClick={handleAddChild}
                                                            className="relative rounded-md hover:text-blue-600 hover:animate-heartbeat"
                                                        >
                                                            <span className="absolute -inset-2.5"/>
                                                            <span className="sr-only">Clear Form</span>
                                                            <PlusCircleIcon aria-hidden="true" className="size-6"/>
                                                        </Button>
                                                    </div>
                                                </div>
                                            </div>

                                            {/*Children Section*/}
                                            <div className="col-span-full relative">
                                                <div className="rounded-lg h-52 overflow-y-scroll border-2 border-dashed">

                                                    {childrenList.length === 0 ? (

                                                        <div className={`w-full h-full flex items-center justify-center`}>

                                                            <div className={`text-center flex flex-col items-center`}>
                                                                <UserPlusIcon className={`text-gray-400 w-6`}/>
                                                                <h3 className="mt-2 text-xs font-semibold">No Children</h3>
                                                                <div className="mt-2">
                                                                    <Button
                                                                        type="button"
                                                                        onClick={handleAddChild}
                                                                        className="inline-flex items-center rounded-md bg-blue-600 px-2 py-1 text-xs font-semibold text-white shadow-sm hover:bg-opacity-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
                                                                    >
                                                                        <PlusIcon aria-hidden="true" className="-ml-0.5 mr-1.5 size-5"/>
                                                                        Add Child
                                                                    </Button>
                                                                </div>
                                                            </div>

                                                        </div>

                                                    ) : (
                                                        <div className={`grid col-span-1 gap-3 p-2`}>

                                                            {childrenList.map((kid, index) => (
                                                                <div key={index} className="items-center justify-start grid grid-cols-8 gap-x-2 gap-y-1">

                                                                    {/*Name*/}
                                                                    <div className={`sm:col-span-4 relative w-full`}>
                                                                        <label
                                                                            className={`absolute bg-white -top-2 left-2 inline-block rounded-lg px-1 text-xs font-medium`}
                                                                        >
                                                                            Name
                                                                        </label>
                                                                        <Input
                                                                            type="string"
                                                                            placeholder="Name..."
                                                                            value={kid.name}
                                                                            onChange={(e) => handleChildChange(index, 'name', e)}
                                                                            className={`block w-full bg-white outline-gray-300 rounded-md px-2 py-1.5 text-base outline outline-1 -outline-offset-1 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-blue-600 sm:text-sm/6`}
                                                                        />

                                                                    </div>

                                                                    {/*Age*/}
                                                                    <div className={`sm:col-span-3 relative w-full`}>
                                                                        <label
                                                                            className={`absolute bg-white -top-2 left-2 inline-block rounded-lg px-1 text-xs font-medium`}
                                                                        >
                                                                            Age
                                                                        </label>
                                                                        <Input
                                                                            type="number"
                                                                            placeholder="Age..."
                                                                            value={kid.age}
                                                                            onChange={(e) => handleChildChange(index, 'age', e)}
                                                                            className={`block w-full bg-white outline-gray-300 rounded-md px-2 py-1.5 text-base outline outline-1 -outline-offset-1 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-blue-600 sm:text-sm/6`}
                                                                        />
                                                                    </div>

                                                                    {/*Delete Button*/}
                                                                    <div className={`sm:col-span-1 flex justify-end w-full`}>
                                                                        <Button type="button"
                                                                            onClick={() => handleRemoveChild(index)}
                                                                        >
                                                                            <XCircleIcon className={`w-6 text-red-500 hover:text-opacity-50`}/>
                                                                        </Button>
                                                                    </div>

                                                                </div>
                                                            ))}

                                                        </div>
                                                    )}

                                                </div>
                                            </div>




                                            {/*Notes*/}
                                            <div className="col-span-full relative">
                                                <label
                                                    className="absolute -top-2 left-2 inline-block rounded-lg bg-white px-1 text-xs font-medium text-gray-900"
                                                >
                                                    Notes
                                                </label>
                                                <Textarea
                                                    type="text"
                                                    value={notes}
                                                    onChange={(e) => setNotes(e.target.value)}
                                                    placeholder="Customer Notes"
                                                    className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-blue-600 sm:text-sm/6"
                                                />
                                            </div>

                                            <div className="col-span-full relative">
                                                <Button
                                                    type="button"
                                                    // onClick={() => setOpen(false)}
                                                    onClick={handleSubmit}
                                                    className="inline-flex w-full justify-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-opacity-50 sm:w-full"
                                                >
                                                    Add Customer
                                                </Button>
                                            </div>

                                        </div>

                                    </div>


                                </div>

                            </DialogPanel>
                        </div>
                    </div>
                </div>
            </Dialog>




        </div>
    )
}

export default Customers;
